import * as XLSX from 'xlsx';
import { financeGet, monthRange, pad2, type FinanceCtx, type FinanceMember, type FinanceRecord } from '../financeApi';

// 엑셀 일괄 입력·통장 입금 매칭이 같이 쓰는 파일 읽기와 성도 매칭.

export type Sheet = { headers: string[]; rows: unknown[][] };

export async function readSheet(file: File): Promise<Sheet> {
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '', raw: true });
  // 은행 거래내역 파일은 위쪽에 계좌 정보 줄이 붙어 있는 경우가 많아, 빈칸이 아닌 칸이 3개 이상인 첫 줄을 머리글로 본다.
  const headerIndex = aoa.findIndex((row) => row.filter((cell) => String(cell ?? '').trim() !== '').length >= 3);
  if (headerIndex < 0) return { headers: [], rows: [] };
  const headers = aoa[headerIndex].map((cell) => String(cell ?? '').trim());
  const rows = aoa.slice(headerIndex + 1).filter((row) => row.some((cell) => String(cell ?? '').trim() !== ''));
  return { headers, rows };
}

export function toDateValue(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }
  const text = String(value ?? '').trim();
  const m = text.match(/(\d{4})\s*[.\-/년]\s*(\d{1,2})\s*[.\-/월]\s*(\d{1,2})/);
  if (m) return `${m[1]}-${pad2(Number(m[2]))}-${pad2(Number(m[3]))}`;
  const compact = text.match(/^(\d{4})(\d{2})(\d{2})/);
  if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`;
  return '';
}

export function toAmount(value: unknown): number {
  if (typeof value === 'number') return Math.round(value);
  const n = Number.parseInt(String(value ?? '').replace(/[^\d-]/g, ''), 10);
  return Number.isInteger(n) ? n : 0;
}

export function cleanName(value: unknown) {
  return String(value ?? '').replace(/\s+/g, '').replace(/님$/, '');
}

export type Match =
  | { kind: 'member'; memberId: number; name: string; guessed: boolean }
  | { kind: 'name'; name: string }
  | { kind: 'anonymous' };

export type MatchResult = { match: Match; candidates: FinanceMember[] };

/**
 * 이름으로 교적 성도를 찾는다. 같은 이름이 한 명이면 자동 연결, 여러 명이면 후보만 돌려주고 사용자가 고른다.
 * 통장 입금자명은 '홍길동십일조'처럼 이름 뒤에 글자가 붙는 경우가 많아, 정확히 일치하는 사람이 없으면
 * 입금자명이 성도 이름으로 시작하는 경우를 '추정'으로 연결한다(화면에 추정 표시).
 */
export function matchMember(rawName: unknown, members: FinanceMember[]): MatchResult {
  const name = cleanName(rawName);
  if (!name || /^(무명|익명)$/.test(name)) return { match: { kind: 'anonymous' }, candidates: [] };

  const exact = members.filter((m) => cleanName(m.name) === name);
  if (exact.length === 1) return { match: { kind: 'member', memberId: exact[0].id, name: exact[0].name, guessed: false }, candidates: exact };
  if (exact.length > 1) return { match: { kind: 'name', name }, candidates: exact };

  const prefix = members
    .filter((m) => cleanName(m.name).length >= 2 && name.startsWith(cleanName(m.name)))
    .sort((a, b) => cleanName(b.name).length - cleanName(a.name).length);
  const longest = prefix.filter((m) => cleanName(m.name).length === cleanName(prefix[0]?.name).length);
  if (longest.length === 1) {
    return { match: { kind: 'member', memberId: longest[0].id, name: longest[0].name, guessed: true }, candidates: longest };
  }
  return { match: { kind: 'name', name }, candidates: longest };
}

/** 입금자명·적요에 헌금 종류가 적혀 있으면('십일조', '감사' 등) 그 항목을 추천한다. */
export function guessCategory(text: string, categories: string[], fallback: string) {
  const compact = text.replace(/\s+/g, '');
  const direct = categories.find((c) => compact.includes(c));
  if (direct) return direct;
  const aliases: [RegExp, string][] = [
    [/십일|십조/, '십일조'],
    [/감사/, '감사헌금'],
    [/건축/, '건축헌금'],
    [/선교/, '선교헌금'],
    [/주일|주정/, '주일헌금'],
    [/부활|추수|성탄|맥추|절기/, '절기헌금'],
    [/구제/, '구제헌금'],
  ];
  const alias = aliases.find(([re, name]) => re.test(compact) && categories.includes(name));
  return alias ? alias[1] : fallback;
}

/** 이미 등록된 내역과 같은 날짜·금액·이름이면 중복 의심. */
export async function loadExistingKeys(ctx: FinanceCtx, dates: string[]) {
  const valid = dates.filter(Boolean).sort();
  if (valid.length === 0) return new Set<string>();
  const from = monthRange(valid[0].slice(0, 7)).from;
  const to = monthRange(valid[valid.length - 1].slice(0, 7)).to;
  const rows = await financeGet<FinanceRecord[]>(ctx, '', { type: '수입', from, to });
  return new Set((rows ?? []).map((r) => recordKey(r.recordDate.slice(0, 10), r.amount, r.donorName)));
}

export function recordKey(date: string, amount: number, name: string) {
  return `${date}|${amount}|${cleanName(name)}`;
}
